import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CourageSpecCardsEditor = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [replaceCardId, setReplaceCardId] = useState(null);

  const resolveUrl = (u) => (u?.startsWith("http") ? u : `${API_BASE}${u}`);

  // ============================================================
  // LOAD ALL (EN, AR, KU)
  // ============================================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VoyahCourageSpecCards?lang=en`),
          axios.get(`${API_BASE}/api/VoyahCourageSpecCards?lang=ar`),
          axios.get(`${API_BASE}/api/VoyahCourageSpecCards?lang=ku`),
        ]);

        const merged = {};
        const ids = [];

        // Build form from EN
        en.data.cards.forEach((c, index) => {
          const cardId = c.id;
          ids.push(cardId);

          merged[`title${cardId}En`] = c.title;
          merged[`desc${cardId}En`] = c.description;
        });

        // AR
        ar.data.cards.forEach((c) => {
          merged[`title${c.id}Ar`] = c.title;
          merged[`desc${c.id}Ar`] = c.description;
        });

        // KU
        ku.data.cards.forEach((c) => {
          merged[`title${c.id}Ku`] = c.title;
          merged[`desc${c.id}Ku`] = c.description;
        });

        setForm(merged);

        // cards array includes id + image
        setCards(en.data.cards);

        setLoading(false);
      } catch (err) {
        console.error("LOAD ERROR:", err);
      }
    };

    load();
  }, []);

  // ============================================================
  // HANDLE CHANGE
  // ============================================================
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // ============================================================
  // SAVE TEXT
  // ============================================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    const dto = {
      cards: cards.map((c) => ({
        cardId: c.id,
        titleEn: form[`title${c.id}En`] || "",
        titleAr: form[`title${c.id}Ar`] || "",
        titleKu: form[`title${c.id}Ku`] || "",

        descriptionEn: form[`desc${c.id}En`] || "",
        descriptionAr: form[`desc${c.id}Ar`] || "",
        descriptionKu: form[`desc${c.id}Ku`] || "",
      })),
    };

    try {
      await axios.put(`${API_BASE}/api/VoyahCourageSpecCards/1`, dto, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed updating text.");
    }
  };

  // ============================================================
  // DELETE IMAGE
  // ============================================================
  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/VoyahCourageSpecCards/1/image`, {
        params: { cardId: id },
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Deleted!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  // ============================================================
  // UPLOAD IMAGE
  // ============================================================
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select a file first.");

    const token = localStorage.getItem("token");
    const fd = new FormData();
    fd.append("ImageFile", uploadFile);
    fd.append("CardId", replaceCardId);

    try {
      await axios.post(`${API_BASE}/api/VoyahCourageSpecCards/1/media`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Uploaded!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Courage Spec Cards Editor</h2>

      {/* LANG FORMS */}
      <div className="row">
        {["En", "Ar", "Ku"].map((lang) => (
          <div className="col-lg-4 col-md-6 mb-4" key={lang}>
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <h5 className="text-primary">{lang} Version</h5>

                {cards.map((card) => (
                  <div key={card.id} className="mb-4">
                    <h6 className="fw-bold">Card {card.id}</h6>

                    <label>Title</label>
                    <input
                      className="form-control mb-2"
                      name={`title${card.id}${lang}`}
                      value={form[`title${card.id}${lang}`] || ""}
                      onChange={handleChange}
                    />

                    <label>Description</label>
                    <textarea
                      rows={3}
                      className="form-control"
                      name={`desc${card.id}${lang}`}
                      value={form[`desc${card.id}${lang}`] || ""}
                      onChange={handleChange}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAVE BUTTON */}
      <div className="text-end mb-5">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save Text
        </button>
      </div>

      {/* IMAGES TABLE */}
      <h3 className="fw-bold mb-3">Card Images</h3>

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Preview</th>
            <th>ID</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {cards.map((c) => (
            <tr key={c.id}>
              <td>
                <img
                  src={resolveUrl(c.image)}
                  alt=""
                  className="img-fluid"
                  style={{ maxHeight: "150px" }}
                />
              </td>
              <td>{c.id}</td>
              <td className="d-flex flex-column gap-2">
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(c.id)}
                >
                  Delete
                </button>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setReplaceCardId(c.id);
                    setShowModal(true);
                  }}
                >
                  Replace
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* UPLOAD MODAL */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Replace Card Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleUpload}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default CourageSpecCardsEditor;
